import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { InsightBar } from '../../components/charts/InsightBar.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { CsvExportButton } from '../../components/data/CsvExportButton.jsx';
import { CsvImportModal } from '../../components/data/CsvImportModal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';
import { countBy, sumBy } from '../../utils/insightStats.js';

import { useAuth } from '../../auth/useAuth.js';

import { EmployeeCreateModal } from './EmployeeCreateModal.jsx';
import { LinkUserModal } from './LinkUserModal.jsx';
import { TerminateEmployeeModal } from './TerminateEmployeeModal.jsx';

const CSV_COLUMNS = [
  { key: 'employeeNumber', label: 'EmployeeNumber' },
  { key: 'firstName', label: 'FirstName' },
  { key: 'lastName', label: 'LastName' },
  { key: 'email', label: 'Email' },
  { key: 'hireDate', label: 'HireDate' },
  { key: 'baseSalary', label: 'BaseSalary' },
  { key: 'currency', label: 'Currency' },
  { key: 'status', label: 'Status' },
];

const STATUS_ORDER = ['ACTIVE', 'ON_LEAVE', 'SUSPENDED', 'TERMINATED'];

function employeeStatusColor(palette, status) {
  if (status === 'ACTIVE') return palette?.series?.[1];
  if (status === 'ON_LEAVE') return palette?.series?.[2];
  if (status === 'SUSPENDED') return palette?.expense;
  return palette?.axis;
}

const CSV_FIELDS = [
  { field: 'firstName', label: 'FirstName', aliases: ['vorname'], required: true },
  { field: 'lastName', label: 'LastName', aliases: ['nachname', 'name'], required: true },
  { field: 'email', label: 'Email', aliases: ['e-mail', 'mail'], required: true },
  { field: 'phone', label: 'Phone', aliases: ['telefon', 'tel'] },
  { field: 'hireDate', label: 'HireDate', aliases: ['eintritt', 'eintrittsdatum'] },
  { field: 'baseSalary', label: 'BaseSalary', aliases: ['lohn', 'gehalt', 'salary'] },
  { field: 'currency', label: 'Currency', aliases: ['währung', 'währung'] },
];

export function EmployeesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [terminateTarget, setTerminateTarget] = useState(null);
  const [linkTarget, setLinkTarget] = useState(null);
  const { roles } = useAuth();
  /* Die Kontenliste hinter der Maske ist dem Firmenadministrator vorbehalten
     (GET /api/users). Die Verknuepfung selbst duerfte auch das Personalwesen
     setzen - ohne die Liste haette es aber nichts zur Auswahl. */
  const darfVerknuepfen = (roles || []).includes('admin');
  const { data: antwort, isLoading, error } = useQuery({
    queryKey: ['hr', 'employees'],
    queryFn: () => hrApi.listEmployees(),
  });
  const data = antwort?.items || [];
  const meta = antwort?.meta;

  const palette = useChartPalette();
  const stats = useMemo(() => {
    const rows = data || [];
    const counts = countBy(rows, (r) => r.status);
    const statuses = [
      ...STATUS_ORDER.filter((s) => counts[s]),
      ...Object.keys(counts).filter((s) => !STATUS_ORDER.includes(s)),
    ];
    const active = counts.ACTIVE || 0;
    const payrollSum = sumBy(rows, (r) => r.baseSalary, (r) => r.status === 'ACTIVE');
    return {
      headcount: rows.length,
      statuses,
      counts,
      active,
      payrollSum,
      avgSalary: active ? payrollSum / active : 0,
    };
  }, [data]);

  const columns = [
    { key: 'employeeNumber', label: t('col.number') },
    { key: 'name',  label: t('col.name'), render: (r) => `${r.firstName} ${r.lastName}` },
    { key: 'email', label: t('col.email') },
    { key: 'hireDate', label: t('hr.employees.col.hired'), render: (r) => formatDate(r.hireDate) },
    {
      key: 'baseSalary',
      label: t('hr.employees.col.salary'),
      align: 'right',
      render: (r) => formatCurrency(r.baseSalary, r.currency),
    },
    { key: 'status', label: t('col.status'), render: (r) => <Badge status={r.status} /> },
    {
      /* Ohne verknuepftes Konto kommt die Person nicht an ihre eigene Zeit,
         ihre Ferien oder ihre Abrechnung heran - der Dienst antwortet mit
         403. Das gehoert deshalb in die Liste und nicht in eine Tiefe. */
      key: 'konto',
      label: t('hr.employees.col.konto'),
      render: (r) =>
        r.userId ? (
          <Badge variant="success">{t('hr.employees.link.ja')}</Badge>
        ) : (
          <Badge variant="warning">{t('hr.employees.link.nein')}</Badge>
        ),
    },
    {
      key: 'actions',
      label: '',
      render: (r) => (
        <div style={{ display: 'inline-flex', gap: 'var(--space-2)' }}>
          {darfVerknuepfen && (
            <Button size="sm" variant="default" onClick={() => setLinkTarget(r)}>
              {t('hr.employees.action.link')}
            </Button>
          )}
          {r.status === 'ACTIVE' && (
            <Button size="sm" variant="danger" onClick={() => setTerminateTarget(r)}>
              {t('hr.employees.action.terminate')}
            </Button>
          )}
        </div>
      ),
    },
  ];

  /* Welche Konten schon zu einer anderen Akte gehoeren. Die Maske waehlt sie
     ab, statt den Fehler erst beim Speichern zu zeigen. */
  const belegteKonten = useMemo(
    () => new Set((data || []).map((r) => r.userId).filter(Boolean)),
    [data],
  );

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('hr.employees.title')}</h1>
          <p className="muted">{t('hr.employees.subtitle')}</p>
        </div>
        <div className="page-actions">
          <CsvExportButton rows={data || []} columns={CSV_COLUMNS} filename="mitarbeiter" />
          <Button variant="default" onClick={() => setImportOpen(true)}>↑ {t('csv.import')}</Button>
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            {t('hr.employees.action.new')}
          </Button>
        </div>
      </header>

      {/* Die Kennzahlen summieren die geholten Zeilen. Sind das nicht alle,
          ist jede Zahl darunter zu klein. */}
      {meta?.total > data.length && (
        <div className="alert warning">
          {t('insight.gekuerzt', { gezeigt: data.length, gesamt: meta.total })}
        </div>
      )}
      {stats.headcount > 0 && (
        <InsightBar
          title={t('insight.byStatus')}
          labels={stats.statuses.map((s) => (s === '-' ? s : t(`status.${s}`)))}
          values={stats.statuses.map((s) => stats.counts[s])}
          colors={stats.statuses.map((s) => employeeStatusColor(palette, s))}
          metrics={[
            { label: t('hr.employees.insight.headcount'), value: stats.headcount },
            { label: t('insight.active'), value: stats.active },
            { label: t('hr.employees.insight.payrollSum'), value: formatCurrency(stats.payrollSum) },
            { label: t('hr.employees.insight.avgSalary'), value: formatCurrency(stats.avgSalary) },
          ]}
        />
      )}
      {error && <div className="alert danger">{error.message}</div>}
      <Table columns={columns} rows={data} isLoading={isLoading} error={error} meta={meta} emptyMessage={t('empty.employees')} />
      <EmployeeCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <TerminateEmployeeModal employee={terminateTarget} onClose={() => setTerminateTarget(null)} />
      <LinkUserModal
        employee={linkTarget}
        belegt={belegteKonten}
        onClose={() => setLinkTarget(null)}
      />
      <CsvImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title={t('hr.employees.action.new')}
        fields={CSV_FIELDS}
        onImportRow={(row) =>
          hrApi.createEmployee({
            firstName: row.firstName,
            lastName: row.lastName,
            email: row.email,
            phone: row.phone || undefined,
            hireDate: row.hireDate || new Date().toISOString().slice(0, 10),
            baseSalary: Number(row.baseSalary || 0),
            currency: row.currency || 'CHF',
          })
        }
        onDone={() => queryClient.invalidateQueries({ queryKey: ['hr', 'employees'] })}
      />
    </section>
  );
}
